<!-- Notification bell: right end of the breadcrumb row (and mobile navbar). Backed by the real API via NotificationBellService -- see notification-bell.service.ts. -->
<div class="notification-bell-wrapper">
  <button type="button" class="notification-bell-btn" (click)="toggleDropdown()" [class.active]="isOpen"
    aria-haspopup="true" [attr.aria-expanded]="isOpen" aria-label="Notifications">
    <i class="bi bi-bell"></i>
    <span class="notification-bell-badge" *ngIf="unreadCount > 0">{{ unreadBadgeLabel }}</span>
  </button>

  <div class="notification-bell-panel" *ngIf="isOpen">
    <div class="notification-bell-panel-arrow"></div>
    <div class="notification-bell-panel-inner">
      <div class="notification-bell-panel-header">
        <span class="notification-bell-panel-title">Notifications</span>
        <span class="notification-bell-panel-subtitle" *ngIf="unreadCount > 0">{{ unreadCount }} new</span>
      </div>

      <div class="notification-bell-list" (scroll)="onDropdownScroll($event)">
        <ng-container *ngIf="notifications.length > 0; else bellEmptyState">
          <div class="notification-bell-item" [class.notification-bell-item--clickable]="!!n.attachmentPath" *ngFor="let n of notifications" (click)="openNotification(n)">
            <div class="notification-bell-item-icon" [ngStyle]="{ 'background-color': resolveIconBackground(n.masterType), 'color': resolveColor(n.masterType) }">
              <i class="bi" [ngClass]="resolveIcon(n.masterType)"></i>
            </div>
            <div class="notification-bell-item-body">
              <div class="notification-bell-item-subject">{{ n.subject }}</div>
              <div class="notification-bell-item-content">{{ n.content }}</div>
              <div class="notification-bell-item-time">{{ timeAgo(n.createdOn) }}</div>
            </div>
            <button type="button" class="notification-bell-item-dismiss" (click)="dismiss(n, $event)" aria-label="Dismiss notification">
              <i class="bi bi-x"></i>
            </button>
          </div>

          <!-- Infinite scroll: next page loads automatically via onDropdownScroll(); this is just the visual cue. -->
          <div class="notification-bell-skeleton-item" *ngIf="dropdownLoading" aria-hidden="true">
            <div class="notification-bell-skeleton-icon"></div>
            <div class="notification-bell-skeleton-body">
              <div class="notification-bell-skeleton-bar notification-bell-skeleton-bar--subject"></div>
              <div class="notification-bell-skeleton-bar notification-bell-skeleton-bar--content"></div>
              <div class="notification-bell-skeleton-bar notification-bell-skeleton-bar--time"></div>
            </div>
          </div>

          <!-- Reached the end of the list (not the same as the big empty state above -- that's
               only for zero notifications from the start). -->
          <div class="notification-bell-caught-up" *ngIf="dropdownExhausted && !dropdownLoading">
            <i class="bi bi-check-circle"></i>
            <span>You're all caught up for today!</span>
          </div>
        </ng-container>

        <ng-template #bellEmptyState>
          <div class="notification-bell-empty">
            <i class="bi bi-check-circle"></i>
            <p>You're all caught up!</p>
            <span>No new notifications right now.</span>
          </div>
        </ng-template>
      </div>

      <div class="notification-bell-panel-footer">
        <button type="button" class="notification-bell-footer-link" (click)="clearAll($event)" [disabled]="notifications.length === 0">
          Clear All
        </button>
        <button type="button" class="notification-bell-footer-link notification-bell-footer-link-primary" (click)="openShowAll($event)">
          Show All
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Show All modal -->
<ng-template #showAllModal>
  <div class="modal-header">
    <h5 class="modal-title" id="notificationBellModalTitle">
      All Notifications
      <span class="notification-bell-modal-title-count" *ngIf="unreadHistory.length > 0">{{ unreadHistory.length }} unread</span>
    </h5>
    <button type="button" class="btn-close" (click)="closeShowAllModal()" aria-label="Close"></button>
  </div>
  <div class="modal-body notification-bell-modal-body" (scroll)="onHistoryScroll($event)">
    <ng-container *ngIf="history.length > 0; else modalEmptyOrLoadingState">

      <div class="notification-bell-section" *ngIf="unreadHistory.length > 0">
        <div class="notification-bell-section-header">
          <span>Unread</span>
          <span class="notification-bell-section-count">{{ unreadHistory.length }}</span>
        </div>
        <div class="notification-bell-modal-item notification-bell-modal-item--unread" [class.notification-bell-modal-item--clickable]="!!n.attachmentPath" [style.border-left-color]="resolveColor(n.masterType)" *ngFor="let n of unreadHistory" (click)="openNotification(n)">
          <div class="notification-bell-item-icon" [ngStyle]="{ 'background-color': resolveIconBackground(n.masterType), 'color': resolveColor(n.masterType) }">
            <i class="bi" [ngClass]="resolveIcon(n.masterType)"></i>
          </div>
          <div class="notification-bell-item-body">
            <div class="notification-bell-item-subject">
              {{ n.subject }}
              <span class="notification-bell-unread-dot" aria-hidden="true"></span>
            </div>
            <div class="notification-bell-item-content">{{ n.content }}</div>
            <div class="notification-bell-item-meta">
              <span class="notification-bell-module-pill" [ngStyle]="{ color: resolveColor(n.masterType) }">{{ moduleLabel(n.masterType) }}</span>
              <span class="notification-bell-item-time">{{ timeAgo(n.createdOn) }} &middot; {{ n.createdOn | date:'dd MMM, hh:mm a' }}</span>
            </div>
          </div>
          <div class="notification-bell-modal-item-actions">
            <button type="button" class="notification-bell-item-dismiss" (click)="dismiss(n, $event)" aria-label="Mark as read" title="Mark as read">
              <i class="bi bi-x"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="notification-bell-section" *ngIf="readHistory.length > 0">
        <div class="notification-bell-section-header">
          <span>Earlier</span>
        </div>
        <div class="notification-bell-modal-item" [class.notification-bell-modal-item--clickable]="!!n.attachmentPath" *ngFor="let n of readHistory" (click)="openNotification(n)">
          <div class="notification-bell-item-icon" [ngStyle]="{ 'background-color': resolveIconBackground(n.masterType), 'color': resolveColor(n.masterType) }">
            <i class="bi" [ngClass]="resolveIcon(n.masterType)"></i>
          </div>
          <div class="notification-bell-item-body">
            <div class="notification-bell-item-subject">{{ n.subject }}</div>
            <div class="notification-bell-item-content">{{ n.content }}</div>
            <div class="notification-bell-item-meta">
              <span class="notification-bell-module-pill" [ngStyle]="{ color: resolveColor(n.masterType) }">{{ moduleLabel(n.masterType) }}</span>
              <span class="notification-bell-item-time">{{ timeAgo(n.createdOn) }} &middot; {{ n.createdOn | date:'dd MMM, hh:mm a' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Infinite scroll: next page loads automatically via onHistoryScroll(); this is just the visual cue. -->
      <ng-container *ngIf="historyLoading">
        <div class="notification-bell-modal-item notification-bell-skeleton-item" *ngFor="let _ of [0, 1]" aria-hidden="true">
          <div class="notification-bell-skeleton-icon"></div>
          <div class="notification-bell-skeleton-body">
            <div class="notification-bell-skeleton-bar notification-bell-skeleton-bar--subject"></div>
            <div class="notification-bell-skeleton-bar notification-bell-skeleton-bar--content"></div>
            <div class="notification-bell-skeleton-bar notification-bell-skeleton-bar--content-short"></div>
            <div class="notification-bell-skeleton-bar notification-bell-skeleton-bar--time"></div>
          </div>
        </div>
      </ng-container>
    </ng-container>

    <ng-template #modalEmptyOrLoadingState>
      <div class="notification-bell-empty notification-bell-empty-modal" *ngIf="!historyLoading">
        <i class="bi bi-bell-slash"></i>
        <p>No notifications yet</p>
        <span>Once you start getting alerts, they'll show up here.</span>
      </div>
      <div class="notification-bell-empty notification-bell-empty-modal" *ngIf="historyLoading">
        <i class="fa fa-spinner fa-spin"></i>
        <p>Loading...</p>
      </div>
    </ng-template>
  </div>
</ng-template>
